import { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import {
  Brain,
  Sparkles,
  Cloud,
  Terminal,
  Box,
  CheckCircle2,
} from "lucide-react";
import { useFeedbackStore } from "../../stores/feedback.js";
import { ActivitySparkline } from "./ActivitySparkline.js";
import { ProgressBar } from "../common/ProgressBar.js";

export interface AgentNodeData {
  label: string;
  agentId: string;
  backend: string;
  status: string;
  costUsd: number;
  tokenUsage: { input: number; output: number };
  capabilities: string[];
  isEntryPoint?: boolean;
  isExitPoint?: boolean;
  /** Layout flow direction — drives which side the edge handles sit on. */
  orientation?: "vertical" | "horizontal";
  [key: string]: unknown;
}

const BACKEND_CONFIG: Record<
  string,
  { icon: any; color: string; borderColor: string; glowClass: string; label: string }
> = {
  "claude-agent-sdk": {
    icon: Brain,
    color: "text-brand-purple",
    borderColor: "border-l-brand-purple",
    glowClass: "shadow-glow-purple",
    label: "Claude",
  },
  "openai-agents": {
    icon: Sparkles,
    color: "text-brand-green",
    borderColor: "border-l-brand-green",
    glowClass: "shadow-glow-green",
    label: "OpenAI",
  },
  "cloudflare-agents": {
    icon: Cloud,
    color: "text-brand-pink",
    borderColor: "border-l-brand-pink",
    glowClass: "shadow-glow-pink",
    label: "Cloudflare",
  },
  "raw-api": {
    icon: Terminal,
    color: "text-brand-cyan",
    borderColor: "border-l-brand-cyan",
    glowClass: "shadow-glow-cyan",
    label: "API",
  },
  custom: {
    icon: Box,
    color: "text-text-muted",
    borderColor: "border-l-border-accent",
    glowClass: "",
    label: "Custom",
  },
};

const STATUS_STYLES: Record<string, { ring: string; glow: string }> = {
  running: { ring: "ring-brand-green/40", glow: "shadow-[0_0_15px_rgba(0,220,100,0.1)]" },
  error: { ring: "ring-brand-pink/40", glow: "shadow-[0_0_15px_rgba(255,0,128,0.1)]" },
  completed: { ring: "ring-brand-cyan/20", glow: "" },
  idle: { ring: "ring-transparent", glow: "" },
};

export const AgentNode = memo(function AgentNode({
  data,
  selected,
}: NodeProps) {
  const d = data as unknown as AgentNodeData;
  const config = BACKEND_CONFIG[d.backend] ?? BACKEND_CONFIG.custom;
  const statusStyle = STATUS_STYLES[d.status] ?? STATUS_STYLES.idle;
  const BackendIcon = config.icon;

  // Get activity data for sparkline
  const agentActivity = useFeedbackStore(
    (s) => s.agentActivity.get(d.agentId),
  );

  const isRunning = d.status === "running";
  const isCompleted = d.status === "completed";
  const isIdle = d.status === "idle";

  // Top-down (hierarchical) layouts connect via Top/Bottom handles so edges
  // flow downward; horizontal layouts (pipeline/star) use Left/Right.
  const targetPos = d.orientation === "vertical" ? Position.Top : Position.Left;
  const sourcePos = d.orientation === "vertical" ? Position.Bottom : Position.Right;

  return (
    <div
      className={`
        group relative rounded-xl border-l-2 ${config.borderColor}
        w-[256px] uw:w-[288px]
        transition-[opacity,box-shadow] duration-300
        ${isIdle ? "opacity-70 hover:opacity-100" : ""}
        ${selected ? "ring-1 ring-brand-purple/40" : ""}
        ${isRunning ? `ring-1 ${statusStyle.ring}` : ""}
        ${statusStyle.glow}
      `}
    >
      {/* Glass background */}
      <div className="absolute inset-0 rounded-xl bg-surface-lighter/50 backdrop-blur-md border border-border-subtle/15" />

      {/* Content */}
      <div className="relative px-4 py-3">
        {/* Connection handles */}
        <Handle
          type="target"
          position={targetPos}
          className="!bg-border-accent !border-surface-lighter !w-2.5 !h-2.5 !opacity-0 group-hover:!opacity-100 !transition-opacity !duration-200"
        />
        <Handle
          type="source"
          position={sourcePos}
          className="!bg-border-accent !border-surface-lighter !w-2.5 !h-2.5 !opacity-0 group-hover:!opacity-100 !transition-opacity !duration-200"
        />

        {/* Header row */}
        <div className="flex items-center gap-2.5 mb-2">
          <div className={`${config.color} shrink-0`}>
            <BackendIcon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-text-primary truncate">
                {d.label}
              </span>
              {d.isEntryPoint && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-green/10 text-brand-green font-medium uppercase tracking-wider">
                  Entry
                </span>
              )}
              {d.isExitPoint && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-cyan/10 text-brand-cyan font-medium uppercase tracking-wider">
                  Exit
                </span>
              )}
            </div>
            <div className="text-[11px] text-text-muted font-mono">
              {config.label}
            </div>
          </div>

          {/* Status indicator */}
          <div className="shrink-0">
            {isRunning && (
              <div className="relative">
                <div className="w-2.5 h-2.5 rounded-full bg-brand-green status-pulse" />
                <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-brand-green animate-ping opacity-30" />
              </div>
            )}
            {isCompleted && (
              <CheckCircle2 className="w-4 h-4 text-brand-cyan" />
            )}
            {d.status === "error" && (
              <div className="w-2.5 h-2.5 rounded-full bg-brand-pink" />
            )}
          </div>
        </div>

        {/* Progress bar (when running) */}
        {isRunning && (
          <ProgressBar
            color="bg-brand-green"
            className="mb-2"
          />
        )}

        {/* Activity sparkline + cost */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1">
            {agentActivity && agentActivity.recentTimestamps.length >= 2 && (
              <ActivitySparkline
                timestamps={agentActivity.recentTimestamps}
                color={config.color.includes("purple") ? "#8000ff" : config.color.includes("green") ? "#00dc64" : config.color.includes("pink") ? "#ff0080" : "#00c8ff"}
              />
            )}
          </div>

          {d.costUsd > 0 && (
            <div className="text-right shrink-0">
              <div className="text-[11px] text-text-primary font-mono">
                ${d.costUsd.toFixed(4)}
              </div>
              <div className="text-[10px] text-text-muted font-mono">
                {formatTokens(d.tokenUsage.input)}in {formatTokens(d.tokenUsage.output)}out
              </div>
            </div>
          )}
        </div>

        {/* Capabilities */}
        {d.capabilities && d.capabilities.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {d.capabilities.slice(0, 4).map((cap) => (
              <span
                key={cap}
                className="text-[9px] px-1.5 py-0.5 rounded bg-surface/40 text-text-muted"
              >
                {cap}
              </span>
            ))}
            {d.capabilities.length > 4 && (
              <span className="text-[9px] text-text-muted">
                +{d.capabilities.length - 4}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M `;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k `;
  return `${n} `;
}
